import { createContext, useCallback, useContext, useEffect } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useSWR, { mutate as mutateGlobal } from "swr";
import { backendAuth } from "@/lib/auth/backend-auth";
import { isAccountInactiveError, isAuthenticationError } from "@/lib/api-errors";
import { APP_CONFIG_SWR_KEY, isCompanyWorkspaceCacheKey } from "@/lib/context-cache-keys";
import type { AuthBootstrapState, AuthContextType, LogoutOptions, Me } from "@/lib/types/auth";
import { getCurrentEnvironment } from "@/lib/environment";
import { hasAppPermission, isAppAdmin } from "@/lib/types/permissions";
import {
  ACCOUNT_INACTIVE_PATH,
  redirectToAccountInactive,
  redirectToSessionExpiredLogin,
} from "@/lib/auth/session-redirect";

const AuthContext = createContext<AuthContextType | null>(null);
const EMPTY_ROLES: string[] = [];
const EMPTY_PERMISSIONS: string[] = [];
const AUTH_ME_SWR_KEY = "auth-me";

interface InactiveAccountTeardownDependencies {
  clearCache: (predicate: (key: unknown) => boolean) => Promise<unknown>;
  hardReplace: (path: string) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function resolveEffectiveMe(me: Me | undefined, error: Error | undefined): Me | undefined {
  if (error != null && (isAuthenticationError(error) || isAccountInactiveError(error))) {
    return undefined;
  }

  return me;
}

export function resolveAuthBootstrapState(
  me: Me | undefined,
  error: Error | undefined,
  isLoading: boolean,
): AuthBootstrapState {
  if (isLoading && me == null) {
    return "loading";
  }

  if (error != null && isAuthenticationError(error)) {
    return "unauthenticated";
  }

  if (error != null && isAccountInactiveError(error)) {
    return "inactive";
  }

  if (me != null) {
    return "authenticated";
  }

  if (error != null) {
    return "unavailable";
  }

  return "unauthenticated";
}

// Session-scoped SWR cache entries that must not survive a user leaving the
// authenticated app (logout or account deactivation).
export function isUserSessionCacheKey(key: unknown, userID: string | null): boolean {
  return (
    key === AUTH_ME_SWR_KEY || key === APP_CONFIG_SWR_KEY || isCompanyWorkspaceCacheKey(key, userID)
  );
}

export async function teardownInactiveAccount(
  userID: string | null,
  dependencies: InactiveAccountTeardownDependencies,
): Promise<void> {
  try {
    await dependencies.clearCache((key) => isUserSessionCacheKey(key, userID));
  } finally {
    dependencies.hardReplace(ACCOUNT_INACTIVE_PATH);
  }
}

async function meFetcher(): Promise<Me> {
  return await backendAuth.getMe();
}

/**
 * Auth Provider Component
 * Manages session state using SWR for automatic caching and revalidation
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();

  const {
    data: me,
    error,
    isLoading,
    mutate,
  } = useSWR<Me, Error>(AUTH_ME_SWR_KEY, meFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 5000,
    shouldRetryOnError: false,
    refreshInterval: 1 * 60 * 1000, // 1 min
    onError: (err: Error) => {
      console.error("[Auth] Me fetch failed:", err);
    },
  });

  const effectiveMe = resolveEffectiveMe(me, error);
  const bootstrapState = resolveAuthBootstrapState(effectiveMe, error, isLoading);

  useEffect(() => {
    if (!isAccountInactiveError(error)) {
      return;
    }
    void teardownInactiveAccount(me?.user_id ?? null, {
      clearCache: async (predicate) => {
        await mutateGlobal(predicate, undefined, false);
      },
      // Shared redirect helper: suppresses the racing session-expired login
      // redirect and no-ops once already on the inactive route.
      hardReplace: () => {
        redirectToAccountInactive();
      },
    }).catch((cacheError: unknown) => {
      console.error("[Auth] Failed to clear inactive-account caches:", cacheError);
    });
  }, [error, me?.user_id]);

  // Check testing environment email restrictions
  useEffect(() => {
    const environment = getCurrentEnvironment();

    // Skip if not in testing mode
    if (environment !== "testing") {
      return;
    }

    // Skip if still loading or no user data
    if (isLoading || effectiveMe == null) {
      return;
    }

    // Skip check if on public routes
    if (typeof window !== "undefined") {
      const pathname = window.location.pathname;
      if (pathname === "/maintenance-restricted" || pathname === "/login") {
        return;
      }
    }

    // Check email domain restriction
    const userEmail = effectiveMe.email;
    if (userEmail) {
      const emailDomain = userEmail.split("@")[1];

      // Only allow @rollforward.com domain during testing environment
      if (emailDomain !== "rollforward.com") {
        console.warn("[Auth] Testing environment: non-rollforward.com email detected, redirecting");
        void navigate("/maintenance-restricted", { replace: true });
      }
    }
  }, [effectiveMe, isLoading, navigate]);

  /**
   * Logout function
   * Calls backend logout and clears local cache
   */
  const logout = async (options?: LogoutOptions): Promise<void> => {
    await mutateGlobal((key) => isUserSessionCacheKey(key, effectiveMe?.user_id ?? null), undefined, false);
    await backendAuth.logout(options);
  };

  const refresh = async (): Promise<void> => {
    await mutate();
  };

  const roles = effectiveMe?.roles ?? EMPTY_ROLES;
  const permissions = effectiveMe?.permissions ?? EMPTY_PERMISSIONS;
  const isAdmin = isAppAdmin(roles);
  const hasAppPermissionHelper = useCallback(
    (permission: string): boolean => hasAppPermission(permissions, permission),
    [permissions],
  );

  const value: AuthContextType = {
    me: effectiveMe ?? null,
    userId: effectiveMe?.user_id ?? null,
    defaultCompanyId: effectiveMe?.default_company_id ?? null,
    roles,
    permissions,
    isAdmin,
    hasAppPermission: hasAppPermissionHelper,
    isLoading,
    error: error ?? null,
    bootstrapState,
    logout,
    refresh,
  };

  // Redirect to login page on authentication error
  useEffect(() => {
    if (error != null && isAuthenticationError(error)) {
      console.warn("[Auth] Me fetch return session expired, redirecting to login...");
      redirectToSessionExpiredLogin();
    }
  }, [error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth hook
 * Access authentication context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
